<template>
  <v-card class="opticalgym_calibration_card" data-testid="calibration">
    <v-card-title style="padding: 0px;" class="calibration-toolbar-wrapper">
      <v-toolbar>
        <v-btn icon @click="close_calibration" data-testid="calibration_close_calibration">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t("calibration.calibrate_device") }}</v-toolbar-title>
      </v-toolbar>
    </v-card-title>
    <v-card-text style="padding: 0px; margin-left: -8px; margin-top: -10px">
      <v-progress-circular v-if="user_metadata_loading" />
      <v-stepper v-else v-model="current_step" non-linear>
        <v-stepper-header>
          <v-stepper-step
            style="cursor: pointer;"
            complete-icon="check"
            :complete="current_step > 1"
            step="1"
            @click="save_calibration(); current_step = 1"
            data-testid="calibration_screen_size_step"
          >
            {{ is_phone_portrait_mode ? $t("calibration.screen_width") : $t("calibration.screen_height") }}
          </v-stepper-step>
          <v-divider />

          <v-stepper-step
            style="cursor: pointer;"
            complete-icon="check"
            :complete="current_step > 2"
            step="2"
            @click="save_calibration(); current_step = 2"
            data-testid="calibration_working_distance_step"
          >
            {{ $t("calibration.working_distance") }}
          </v-stepper-step>
          <v-divider />

          <template v-if="!different_glasses_disallowed">
            <v-stepper-step
              style="cursor: pointer;"
              complete-icon="check"
              :complete="current_step > 3"
              step="3"
              @click="save_calibration(); current_step = 3"
              data-testid="calibration_anaglyph_glasses_step"
            >
              {{ $t("calibration.anaglyph_glasses") }}
            </v-stepper-step>
            <v-divider />
          </template>

          <v-stepper-step
            style="cursor: pointer;"
            complete-icon="check"
            :step="colour_calibration_step"
            @click="save_calibration(); current_step = colour_calibration_step"
            data-testid="calibration_colour_step_header"
          >
            {{ $t("calibration.colour_calibration") }}
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-container style="min-height: 480px">
              <v-layout row wrap>
                <v-flex xs12>
                  <h3>{{ is_phone_portrait_mode ? $t("calibration.screen_width") : $t("calibration.screen_height") }}</h3>
                  <p class="calibration-instruction mt-4">
                    {{ phone_screen ? (is_phone_portrait_mode ? $t("calibration.screen_width_quarter_text") : $t("calibration.screen_height_quarter_text")) : $t("calibration.screen_height_text") }}
                  </p>
                  <v-container fluid grid-list-lg>
                    <v-layout row wrap>
                      <v-flex xs12 md4>
                        <div data-testid="calibration_screen_size">
                          <DistanceSelector
                            v-model="height_cm"
                            class="calibration_distance_selector"
                            :min="distance_selector_min"
                            :max="distance_selector_max"
                            :step="distance_selector_step"
                          />
                        </div>

                        <v-switch
                          v-if="isTouchDevice && !phone_screen"
                          v-model="is_touch_screen"
                          :label="$t('calibration.touch_screen')"
                        />
                      </v-flex>
                      <v-flex xs12 md8 class="calibration_reference_image_wraper">
                        <img
                          id="calibration_reference_image"
                          :src="phone_screen
                            ? '/assets/us_quarter.png'
                            : '/assets/standard-card.' + $i18n.lang() + '.png'"
                        />
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-flex>
              </v-layout>
            </v-container>

            <v-btn
              color="primary"
              class="calibration_continue"
              @click="save_calibration(); current_step = 2"
              data-testid="calibration_continue_to_step_2"
            >
              {{ $t("common.continue") }}
            </v-btn>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-container style="min-height: 480px">
              <v-layout row wrap justify-space-around>
                <v-flex md6 xs12>
                  <h3>{{ $t("calibration.working_distance") }}</h3>
                  <p class="calibration-instruction mt-4">{{ $t("calibration.working_distance_text") }}</p>
                  <v-container fluid grid-list-lg>
                    <div data-testid="calibration_working_distance">
                      <DistanceSelector
                        v-model="distance_cm"
                        class="calibration_working_distance_selector"
                        :min="10"
                        :max="500"
                        @change="distance_manually_changed"
                      />
                    </div>
                  </v-container>
                </v-flex>
              </v-layout>
            </v-container>

            <v-btn
              color="primary"
              class="calibration_continue"
              @click="save_calibration(); current_step = 3"
              data-testid="calibration_continue_from_working_distance"
            >
              {{ $t("common.continue") }}
            </v-btn>
          </v-stepper-content>

          <v-stepper-content v-if="!different_glasses_disallowed" step="3">
            <v-container style="min-height: 480px">
              <v-layout row wrap justify-space-around>
                <v-flex xs12 md12>
                  <h3>{{ $t("calibration.anaglyph_glasses") }}</h3>
                  <p class="calibration-instruction mt-4">{{ $t("calibration.anaglyph_glasses_text") }}</p>
                  <v-container class="calibration_select_anaglyph_glasses">
                    <div class="anaglyph-grid">
                      <div
                        class="anaglyph-item selector-red-cyan"
                        :class="anaglyph_orientation"
                        data-testid="calibration_anaglyph_option"
                        data-orientation="red-cyan"
                        @click="change_anaglyph_orientation('red-cyan')"
                      >
                        <div class="align-center">{{ $t("calibration.red_left_blue_right") }}</div>
                        <img src="/assets/red-cyan.png" />
                      </div>

                      <div
                        class="anaglyph-item selector-cyan-red"
                        :class="anaglyph_orientation"
                        data-testid="calibration_anaglyph_option"
                        data-orientation="cyan-red"
                        @click="change_anaglyph_orientation('cyan-red')"
                      >
                        <div class="align-center">{{ $t("calibration.blue_left_red_right") }}</div>
                        <img src="/assets/cyan-red.png" />
                      </div>

                      <div
                        class="anaglyph-item selector-green-red"
                        :class="anaglyph_orientation"
                        data-testid="calibration_anaglyph_option"
                        data-orientation="green-red"
                        @click="change_anaglyph_orientation('green-red')"
                      >
                        <div class="align-center">{{ $t("calibration.green_left_red_right") }}</div>
                        <img src="/assets/green-red.png" />
                      </div>

                      <div
                        class="anaglyph-item selector-red-green"
                        :class="anaglyph_orientation"
                        data-testid="calibration_anaglyph_option"
                        data-orientation="red-green"
                        @click="change_anaglyph_orientation('red-green')"
                      >
                        <div class="align-center">{{ $t("calibration.red_left_green_right") }}</div>
                        <img src="/assets/red-green.png" />
                      </div>
                    </div>
                  </v-container>
                </v-flex>
              </v-layout>
            </v-container>

            <v-btn
              color="primary"
              class="calibration_continue"
              @click="save_calibration(); current_step = 4"
              data-testid="calibration_continue_to_step_4"
            >
              {{ $t("common.continue") }}
            </v-btn>
            <v-btn
              :small="!phone_screen"
              :x-small="phone_screen"
              :class="phone_screen ? 'ml-2' : 'ml-6'"
              @click="save_calibration(); close_calibration();"
              data-testid="calibration_dont_have_glasses"
            >
              {{ $t("calibration.dont_have_glasses") }}
            </v-btn>
          </v-stepper-content>

          <v-stepper-content :step="colour_calibration_step">
            <v-container style="min-height: 470px">
              <v-layout row wrap justify-space-around>
                <v-flex xs12 md12>
                  <h3>{{ $t("calibration.colour_calibration") }}</h3>
                  <ol class="calibration-instruction mt-4">
                    <li>
                      {{ $t("calibration.put_anaglyph_glasses") }}
                      <img
                        src="/assets/red-blue-glasses-icon.png"
                        style="height: 14px"
                      />
                    </li>
                    <li>{{ $t("calibration.covering_one_eye_text") }}</li>
                  </ol>
                  <v-container
                    fluid
                    class="calibration_colour_calibration"
                    data-testid="calibration_colour_step"
                  >
                    <v-layout v-if="anaglyph_orientation == 'cyan-red'" justify-center>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="red_brightness"
                          side="left"
                          colour="red"
                          opposite_colour="blue"
                        />
                      </v-flex>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="blue_brightness"
                          side="right"
                          colour="blue"
                          opposite_colour="red"
                        />
                      </v-flex>
                    </v-layout>
                    <v-layout v-if="anaglyph_orientation == 'red-cyan'" justify-center>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="blue_brightness"
                          side="left"
                          colour="blue"
                          opposite_colour="red"
                        />
                      </v-flex>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="red_brightness"
                          side="right"
                          colour="red"
                          opposite_colour="blue"
                        />
                      </v-flex>
                    </v-layout>

                    <v-layout v-if="anaglyph_orientation == 'green-red'" justify-center>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="red_brightness"
                          side="left"
                          colour="red"
                          opposite_colour="green"
                        />
                      </v-flex>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="green_brightness"
                          side="right"
                          colour="green"
                          opposite_colour="red"
                        />
                      </v-flex>
                    </v-layout>
                    <v-layout v-if="anaglyph_orientation == 'red-green'" justify-center>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="green_brightness"
                          side="left"
                          colour="green"
                          opposite_colour="red"
                        />
                      </v-flex>
                      <v-flex xs6 md6>
                        <CalibrationColour
                          v-model="red_brightness"
                          side="right"
                          colour="red"
                          opposite_colour="green"
                        />
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-flex>
              </v-layout>
            </v-container>
            <v-btn
              class="calibration_continue calibration_done"
              color="primary"
              @click="save_calibration(); close_calibration();"
              data-testid="calibration_done"
            >
              {{ $t("common.done") }}
            </v-btn>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import { Calibration } from "../classes/calibration";
import { effectiveAnaglyphOrientation } from "../lib/anaglyph_orientation";
import {
  CREDIT_CARD_HEIGHT_CM,
  DEFAULT_CALIBRATION_BRIGHTNESS,
  US_QUARTER_HEIGHT_CM,
  calibrationMatchesCurrentDevice,
  calibrationReferenceImageHeightPx,
  clampCalibrationBrightness,
  guessCalibrationMeasurements,
  guessedCalibrationDistanceCm,
  viewportFillsScreen,
} from "../lib/calibration_core";
import {
  exitFullscreen,
  fullscreenElement,
  requestFullscreen,
} from "../lib/fullscreen";
import DistanceSelector from "./DistanceSelector.vue";
import CalibrationColour from "./CalibrationColour.vue";
import Vue from "vue";

import jQuery from "jquery";

export default {
  components: { DistanceSelector, CalibrationColour },
  data() {
    return {
      current_step: 1,
      // Last measurement of the screen's short edge that could be trusted; see
      // `measure_screen_short_edge_css_px()`. Seeded with the non-fullscreen
      // fallback and replaced as soon as fullscreen lands.
      screen_short_edge_css_px: Math.min(screen.width, screen.height),
      ...wizard_seed_values(this.$store.state),
    };
  },
  computed: {
    different_glasses_disallowed() {
      return Boolean(this.current_clinic_prefs?.disallow_different_glasses);
    },
    colour_calibration_step() {
      return this.different_glasses_disallowed ? 3 : 4;
    },
    isTouchDevice() {
      return navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
    },
    is_phone_portrait_mode() {
      // visualViewport is guarded here as it is everywhere else in this file:
      // the layout viewport is the right fallback for orientation, which only
      // needs the two axes compared.
      const viewport = window.visualViewport ||
        { width: window.innerWidth, height: window.innerHeight };
      return viewport.width < viewport.height && this.isTouchDevice && this.phone_screen;
    },
    distance_selector_min() {
      return this.phone_screen ? 5 : 10;
    },
    distance_selector_max() {
      return this.phone_screen ? 30 : 150;
    },
    distance_selector_step() {
      return this.phone_screen ? 0.5 : 1;
    },
    ...mapState(["user_calibrations", "user_metadata_loading", "current_user", "current_calibration", "current_clinic_prefs", "app_config", "phone_screen"]),
  },
  watch: {
    anaglyph_orientation(value) {
      this.$store.commit("setCalibrationDraftOrientation", value);
    },
    user_metadata_loading(loading) {
      // The wizard can be opened before the user's calibrations have arrived,
      // in which case `data()` could only seed it with the canonical estimates.
      // Nothing is editable behind the loading spinner, so there is no user
      // input to preserve here.
      if (loading) return;
      Object.assign(this, wizard_seed_values(this.$store.state));
    },
    "$store.state.calibration_draft_orientation"(value) {
      if (value) this.anaglyph_orientation = value;
    },
    height_cm: function () {
      if (!this.calibrate_distance_set) {
        this.distance_cm = guessedCalibrationDistanceCm(this.height_cm);
      }

      // Set the image size manually so we have total control
      this.set_image_size();
    },
    current_clinic_prefs: {
      handler() {
        this.anaglyph_orientation = effectiveAnaglyphOrientation(
          this.current_calibration?.anaglyph_orientation,
          this.current_clinic_prefs,
          this.app_config,
        );
      },
      deep: true,
    },
    colour_calibration_step(new_step, old_step) {
      if (this.current_step === old_step) {
        this.current_step = new_step;
      }
    },
    current_calibration(calibration, previous) {
      const previous_orientation = effectiveAnaglyphOrientation(
        previous?.anaglyph_orientation, this.current_clinic_prefs, this.app_config,
      );
      // A delayed local save must not overwrite a newer unsaved glasses choice.
      // Remote saves replace the whole draft through calibration_sync_revision.
      if (this.anaglyph_orientation === previous_orientation) {
        this.anaglyph_orientation = effectiveAnaglyphOrientation(
          calibration?.anaglyph_orientation, this.current_clinic_prefs, this.app_config,
        );
      }
    },
    "$store.state.calibration_sync_revision"() {
      const calibration = this.current_calibration;
      if (!calibration) return;
      // A synchronized distance is an explicit choice, not a size-based guess.
      this.calibrate_distance_set = true;
      this.height_cm = calibration.height_cm;
      this.distance_cm = calibration.distance_cm;
      this.anaglyph_orientation = effectiveAnaglyphOrientation(
        calibration.anaglyph_orientation, this.current_clinic_prefs, this.app_config,
      );
      this.red_brightness = brightness_to_slider(calibration.colour_correction.red_brightness);
      this.blue_brightness = brightness_to_slider(calibration.colour_correction.blue_brightness);
      this.green_brightness = brightness_to_slider(calibration.colour_correction.green_brightness);
      this.is_touch_screen = calibration.no_touch_screen === null
        ? Boolean(this.isTouchDevice) : !calibration.no_touch_screen;
    },
  },
  mounted() {
    this.$store.commit("setCalibrationDraftOrientation", this.anaglyph_orientation);
    this.handle_viewport_change = () => {
      Vue.nextTick(() => {
        this.set_image_size();
      });
    };
    window.addEventListener("resize", this.handle_viewport_change);
    // Entering and leaving fullscreen changes which measurement the reference
    // image is sized from, so re-measure on both. Browsers fire this before the
    // new viewport dimensions have settled (and macOS animates the transition),
    // so the `resize` listener above is what lands the final size.
    document.addEventListener("fullscreenchange", this.handle_viewport_change);
    document.addEventListener("webkitfullscreenchange", this.handle_viewport_change);
    if (window.visualViewport) {
      // Pinch-zoom and panning don't reliably fire `window`'s `resize` event,
      // but do fire on `visualViewport`.
      window.visualViewport.addEventListener("resize", this.handle_viewport_change);
      window.visualViewport.addEventListener("scroll", this.handle_viewport_change);
    }

    // Fullscreen makes the viewport the screen, which is the only way to read
    // the screen's size in live CSS pixels across every browser — see
    // `measure_screen_short_edge_css_px()`. Requested here rather than from the
    // button that opens the dialog because the dialog content is `v-if`'d, so
    // this runs inside the opening click's user activation. Declining is normal
    // (iOS, iframes without `allow="fullscreen"`): the measurement falls back.
    requestFullscreen(this.$el).then(() => {
      this.set_image_size();
    });

    // Set the image size manually so we have total control
    Vue.nextTick(() => {
      this.set_image_size();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handle_viewport_change);
    document.removeEventListener("fullscreenchange", this.handle_viewport_change);
    document.removeEventListener("webkitfullscreenchange", this.handle_viewport_change);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.handle_viewport_change);
      window.visualViewport.removeEventListener("scroll", this.handle_viewport_change);
    }
    exitFullscreen();
  },
  methods: {
    set_image_size() {
      // A viewport that can't be trusted right now (an on-screen keyboard is
      // open, say) leaves the last trustworthy measurement in place rather than
      // resizing the reference image to a number we know is wrong.
      const measured = measure_screen_short_edge_css_px();
      if (measured !== null) {
        this.screen_short_edge_css_px = measured;
      }

      // Viewport events can fire while the wizard is on another step, so the
      // reference image isn't always in the DOM.
      var image = document.getElementById("calibration_reference_image");
      if (image) {
        // Called directly rather than through a computed property: the height
        // depends on live viewport state (visualViewport.scale, screen size)
        // that Vue can't track, so a computed would serve a stale cached value
        // when only the pinch level changed.
        image.style.height = calibrationReferenceImageHeightPx({
          item_cm: this.phone_screen ? US_QUARTER_HEIGHT_CM : CREDIT_CARD_HEIGHT_CM,
          screen_short_edge_css_px: this.screen_short_edge_css_px,
          screen_cm: this.height_cm,
          pinch_scale: window.visualViewport ? window.visualViewport.scale : 1,
        }) + "px";
      }
    },
    change_anaglyph_orientation(new_orientation) {
      if (this.different_glasses_disallowed) {
        return;
      }
      this.anaglyph_orientation = new_orientation;
    },
    distance_manually_changed: function () {
      this.calibrate_distance_set = true;
    },

    close_calibration: function () {
      this.$store.commit("setCalibrationDialog", false);
      this.current_step = 1;
    },
    save_calibration: function () {
      var current_calibration;

      if (this.current_calibration) {
        current_calibration = Calibration.fromJson(JSON.parse(JSON.stringify(this.current_calibration)));
        // The screen can change while the wizard is open. Saving would then
        // carry this record's id while refreshDeviceIdentity() rewrites it to
        // the screen in front of us, overwriting a calibration the user still
        // needs for the screen they left. Save as a new record instead.
        if (!calibrationMatchesCurrentDevice(current_calibration)) {
          current_calibration.id = null;
          current_calibration.user_id = null;
        }
      } else {
        current_calibration = new Calibration(
          this.current_clinic_prefs,
          this.app_config,
        );
      }
      current_calibration = Object.assign(current_calibration, {
        height_cm: this.height_cm,
        distance_cm: this.distance_cm,
        anaglyph_orientation: effectiveAnaglyphOrientation(
          this.anaglyph_orientation,
          this.current_clinic_prefs,
          this.app_config,
        ),
        colour_correction: {
          red_brightness: this.red_brightness / 100,
          blue_brightness: this.blue_brightness / 100,
          green_brightness: this.green_brightness / 100,
        },
      });

      if (this.isTouchDevice && !this.is_touch_screen) {
        current_calibration.no_touch_screen = true;
      } else {
        current_calibration.no_touch_screen = false;
      }

      current_calibration.refreshDeviceIdentity();

      return this.$store.dispatch("updateCurrentCalibration", current_calibration);
    },
  },
};

// Utility Functions
// -----------------

/**
 * The wizard's editable values as they start out for a given store state: the
 * user's saved calibration where there is one, and the canonical estimates
 * where there is not.
 *
 * Separate from `data()` because the wizard can be opened before the user's
 * calibrations have finished loading, in which case it is seeded a second time
 * once they arrive — see the `user_metadata_loading` watcher.
 */
function wizard_seed_values(state) {
  const calibration_guess = guessCalibrationMeasurements();
  const clinic_prefs = state.current_clinic_prefs;
  const app_config = state.app_config || window.app_config;

  const values = {
    height_cm: calibration_guess.height_cm,
    distance_cm: calibration_guess.distance_cm,
    anaglyph_orientation: effectiveAnaglyphOrientation(null, clinic_prefs, app_config),
    calibrate_distance_set: false, // Has the user yet to manually calibrate the distance?
    red_brightness: brightness_to_slider(DEFAULT_CALIBRATION_BRIGHTNESS),
    blue_brightness: brightness_to_slider(DEFAULT_CALIBRATION_BRIGHTNESS),
    green_brightness: brightness_to_slider(DEFAULT_CALIBRATION_BRIGHTNESS),
    is_touch_screen: navigator.maxTouchPoints && navigator.maxTouchPoints > 2,
  };

  const calibration = state.current_calibration;
  // Colour and glasses carry over from any of the user's screens; the
  // measurements only make sense for the screen they were taken on.
  const visual_calibration =
    calibration || state.user_calibrations.getMostRecentCalibration();

  if (calibration) {
    values.height_cm = calibration.height_cm;
    values.distance_cm = calibration.distance_cm;
    if (calibration.no_touch_screen === true || calibration.no_touch_screen === false) {
      values.is_touch_screen = !calibration.no_touch_screen;
    }
  }

  if (visual_calibration) {
    const colour_correction = visual_calibration.colour_correction || {};
    values.anaglyph_orientation = effectiveAnaglyphOrientation(
      visual_calibration.anaglyph_orientation,
      clinic_prefs,
      app_config,
    );
    values.red_brightness = brightness_to_slider(colour_correction.red_brightness);
    values.blue_brightness = brightness_to_slider(colour_correction.blue_brightness);
    values.green_brightness = brightness_to_slider(colour_correction.green_brightness);
  }

  return values;
}

/**
 * A stored 0-1 brightness as the whole-percent value its slider takes.
 *
 * Rounded, not truncated: the sliders are saved straight back out, and
 * 0.29 * 100 is 28.999999999999996, so truncating would walk a saved brightness
 * down a step every time the wizard is reopened.
 */
function brightness_to_slider(brightness) {
  return Math.round(clampCalibrationBrightness(brightness) * 100);
}

/**
 * The screen's short edge in live CSS pixels — the unit `style.height` is
 * interpreted in, so the only correct basis for sizing the reference image —
 * or `null` when no trustworthy measurement is available at this moment.
 *
 * In fullscreen the viewport is the screen, so the layout viewport gives this
 * directly and correctly in every browser at any zoom level, but only while the
 * viewport still covers the screen. It may not: the wizard has a number field
 * the user can focus, and on Android Chrome and Firefox an on-screen keyboard
 * shrinks the layout viewport without leaving fullscreen, which would otherwise
 * read as a screen a third of its real size. `viewportFillsScreen()` rejects
 * that, and the caller keeps its previous measurement instead. (`innerWidth`/
 * `innerHeight` rather than `visualViewport`: the visual viewport also shrinks
 * for pinch, which is handled separately as a scale factor.)
 *
 * Outside fullscreen the viewport excludes browser chrome, which can't be
 * measured in CSS pixels, so fall back to `screen.*`. That fallback is exact in
 * Firefox, which reports `screen.*` in live CSS pixels, and in any browser at
 * 100% zoom; it is wrong by the zoom factor in a zoomed Chrome or Safari, which
 * is precisely where fullscreen is available. Checked per call rather than
 * cached because the user can leave fullscreen at any time via Esc or F11.
 */
function measure_screen_short_edge_css_px() {
  if (!fullscreenElement()) {
    return Math.min(screen.width, screen.height);
  }

  const fills_screen = viewportFillsScreen({
    screen_width: screen.width,
    screen_height: screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  });

  return fills_screen ? Math.min(window.innerWidth, window.innerHeight) : null;
}
</script>


<style>
.opticalgym_calibration_card .selector-red-cyan.red-cyan,
.opticalgym_calibration_card .selector-cyan-red.cyan-red,
.opticalgym_calibration_card .selector-green-red.green-red,
.opticalgym_calibration_card .selector-red-green.red-green {
  font-weight: bold;
  box-shadow: 1px 1px 2px black, 0 0 25px blue, 0 0 5px darkblue;
}

.opticalgym_calibration_card .selector-red-cyan,
.opticalgym_calibration_card .selector-cyan-red,
.opticalgym_calibration_card .selector-green-red,
.opticalgym_calibration_card .selector-red-green {
  cursor: pointer;
}

.opticalgym_calibration_card .calibration-toolbar-wrapper {
  padding: 0px;
}

.opticalgym_calibration_card .calibration_reference_image_wraper {
  text-align: center;
}

.opticalgym_calibration_card .align-center {
  text-align: center;
}

.opticalgym_calibration_card .v-stepper__header {
  height: 55px;
}

.opticalgym_calibration_card .v-stepper__step {
  margin-top: -5px;
  padding-top: 0px;
  padding-bottom: 0px;
}

.opticalgym_calibration_card .calibration-instruction {
  font-size: larger;
}

.phone-screen .opticalgym_calibration_card .calibration-instruction {
  font-size: 0.9rem;
}

.opticalgym_calibration_card .calibration_select_anaglyph_glasses {
  max-height: 600px;
}

.opticalgym_calibration_card .anaglyph-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  justify-items: center;
}

.opticalgym_calibration_card .anaglyph-item {
  max-width: 200px;
  max-height: 250px;
  width: 100%;
  padding: 16px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border 0.2s ease;
}

.phone-screen .opticalgym_calibration_card .anaglyph-item {
  max-width: 90%;
  max-height: 150px;
  padding-top: 8px;
}

.phone-screen .opticalgym_calibration_card .anaglyph-item img{
  max-width: 85%;
  margin-left: 10%;
}

.opticalgym_calibration_card .anaglyph-item img {
  max-width: 100%;
  max-height: 100%;
}

.opticalgym_calibration_card .anaglyph-item:hover {
  border: 1px solid #ccc;
}

.phone-screen .calibration_colour_calibration {
  padding: 0px;
  margin-bottom: 10px;
}
</style>
